import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, Sparkles, ArrowLeft, Wallet, CreditCard, ShieldCheck, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { payWithRazorpay } from "@/lib/razorpay";
import { fmtNum, fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useTrial } from "@/components/TrialGuard";
import { Logo } from "@/components/Logo";
import { Kpi, Panel, Badge2, PageHead } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import BackToTop from "@/components/BackToTop";
import TrustBar from "@/components/TrustBar";

const inr = (v) => `₹${Number(v).toLocaleString("en-IN")}`;

export default function Billing() {
  const { user } = useAuth();
  const { status: trial, refresh: refreshTrial } = useTrial();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [me, setMe] = useState(null);
  const [admin, setAdmin] = useState(null);

  const load = () => {
    api.get("/billing/products").then(({ data }) => setProducts(data));
    api.get("/billing/me").then(({ data }) => setMe(data));
    api.get("/admin/trials").then(({ data }) => setAdmin(data)).catch(() => setAdmin(false));
  };
  useEffect(load, []);

  const extend = async (id, days) => {
    try {
      await api.post(`/admin/trials/${id}/extend`, { days });
      load();
    } catch (e) {
      /* non-admins never see this control */
    }
  };

  const buy = (product) =>
    payWithRazorpay({ product, user, onSuccess: () => { load(); refreshTrial(); } });

  const plans = products.filter((p) => p.kind === "subscription");
  const credits = products.filter((p) => p.kind === "credits");
  const licence = products.find((p) => p.kind === "license");

  return (
    <div className="min-h-screen" data-testid="billing-page">
      <header className="h-14 border-b border-border flex items-center gap-3 px-4 sm:px-6 sticky top-0 z-20 bg-background/85 backdrop-blur-md">
        <Logo size="sm" />
        <button onClick={() => navigate("/projects")} className="ml-auto text-[13px] text-muted-foreground hover:text-foreground flex items-center gap-1.5" data-testid="billing-back-btn">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to portfolio
        </button>
      </header>

      <div className="p-4 sm:p-6 max-w-[1200px] mx-auto">
        {trial?.state === "trial" ? (
          <div className="border border-[#F97316]/40 bg-[#F97316]/10 p-3 mb-5 flex flex-wrap items-center gap-3" data-testid="trial-banner">
            <Clock className="w-4 h-4 text-[#F97316]" />
            <span className="text-[13px]">
              <strong>{trial.days_remaining} {trial.days_remaining === 1 ? "day" : "days"} left</strong> in your free trial — every planning and execution module is open. AI modules unlock on a paid plan.
            </span>
            <a href="#plans" className="ml-auto text-[13px] text-[#F97316] hover:underline">Upgrade now →</a>
          </div>
        ) : trial?.state === "expired" ? (
          <div className="border border-red-500/40 bg-red-500/10 p-3 mb-5 flex flex-wrap items-center gap-3" data-testid="expired-banner">
            <Clock className="w-4 h-4 text-red-500" />
            <span className="text-[13px]"><strong>Your trial has expired.</strong> Pick a plan below to restore access to your projects.</span>
          </div>
        ) : null}

        <PageHead title="Plans & Billing" subtitle="Straightforward INR pricing. UPI, cards, netbanking and wallets via Razorpay.">
          <Badge2 tone={me?.plan ? "good" : "warn"} testId="current-plan-badge">
            {me?.plan ? `${me.plan} plan` : trial?.state === "trial" ? "free trial" : "no active plan"}
          </Badge2>
        </PageHead>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
          <Kpi label="Current plan" value={me?.plan ? me.plan : trial?.state === "trial" ? "Trial" : "—"} tone="primary" testId="kpi-plan" sub={me?.plan_expires_at ? `Renews ${fmtDate(me.plan_expires_at)}` : null} />
          <Kpi label="AI credit balance" value={fmtNum(me?.ai_credits || 0, 0)} icon={Wallet} testId="kpi-credits" sub="Used by BOQ parsing & risk briefs" />
          <Kpi label="Project allowance" value={me?.project_limit ? me.project_limit : "Unlimited"} />
          <Kpi label="Licensed projects" value={me?.licensed_projects?.length || 0} />
        </div>

        <div id="plans" className="grid md:grid-cols-2 gap-3">
          {plans.map((p) => {
            const pro = p.id === "plan_professional";
            return (
              <div key={p.id} className={`border p-5 flex flex-col rise ${pro ? "border-[#F97316]" : "border-border"} bg-card`} data-testid={`plan-card-${p.id}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-xl font-semibold">{p.label}</h3>
                  {pro ? <Badge2 tone="warn">Most popular</Badge2> : null}
                </div>
                <div className="mt-3 flex items-end gap-1.5">
                  <span className="num text-3xl font-semibold">{inr(p.amount_inr)}</span>
                  <span className="text-[13px] text-muted-foreground mb-1">/ month</span>
                </div>
                <ul className="mt-4 space-y-1.5 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-[13px]">
                      <Check className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${pro ? "text-[#F97316]" : "text-emerald-500"}`} />
                      {f}
                    </li>
                  ))}
                </ul>
                <Button className="mt-5" variant={pro ? "default" : "outline"} onClick={() => buy(p)} data-testid={`buy-${p.id}-btn`}>
                  <CreditCard className="w-4 h-4 mr-1.5" /> Subscribe — {inr(p.amount_inr)}
                </Button>
              </div>
            );
          })}
        </div>

        <div className="grid md:grid-cols-3 gap-3 mt-3">
          {credits.map((c) => (
            <div key={c.id} className="border border-border bg-card p-4 flex flex-col" data-testid={`credit-pack-${c.id}`}>
              <Sparkles className="w-5 h-5 text-[#F97316]" />
              <h4 className="font-semibold mt-2">{c.label}</h4>
              <p className="text-[12px] text-muted-foreground mt-1 flex-1">{c.features.join(" · ")}</p>
              <Button size="sm" variant="outline" className="mt-3" onClick={() => buy(c)} data-testid={`buy-${c.id}-btn`}>
                Top up {inr(c.amount_inr)}
              </Button>
            </div>
          ))}
          {licence ? (
            <div className="border border-border bg-card p-4 flex flex-col" data-testid="licence-card">
              <ShieldCheck className="w-5 h-5 text-primary" />
              <h4 className="font-semibold mt-2">{licence.label}</h4>
              <p className="text-[12px] text-muted-foreground mt-1 flex-1">{licence.features.join(" · ")}</p>
              <Button size="sm" variant="outline" className="mt-3" onClick={() => buy(licence)} data-testid="buy-project_license-btn">
                Buy licence {inr(licence.amount_inr)}
              </Button>
            </div>
          ) : null}
        </div>

        <Panel title="Payment history" className="mt-5" testId="billing-history-panel">
          <div className="overflow-x-auto">
            <table className="w-full dense-table">
              <thead><tr><th>Date</th><th>Item</th><th>Order</th><th>Payment</th><th className="text-right">Amount</th><th>Status</th></tr></thead>
              <tbody>
                {(me?.transactions || []).map((t) => (
                  <tr key={t.order_id} data-testid={`txn-row-${t.order_id}`}>
                    <td className="num">{fmtDate(t.created_at)}</td>
                    <td>{t.product_label}</td>
                    <td className="num text-[11px] text-muted-foreground">{t.order_id}</td>
                    <td className="num text-[11px] text-muted-foreground">{t.payment_id || "—"}</td>
                    <td className="num text-right">{inr(t.amount / 100)}</td>
                    <td>
                      <Badge2 tone={t.status === "paid" ? "good" : t.status === "created" ? "warn" : "bad"}>{t.status}</Badge2>
                    </td>
                  </tr>
                ))}
                {!me?.transactions?.length ? <tr><td colSpan={6} className="text-muted-foreground">No payments yet.</td></tr> : null}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground mt-3">
            Test mode is active — use card <span className="num">4111 1111 1111 1111</span> with any future expiry and CVV, or the
            <span className="num"> success@razorpay</span> UPI VPA. No real money moves.
            <Link to="/about" className="text-primary hover:underline ml-1">About Plan-Sarthi</Link>
          </p>
        </Panel>

        {admin ? (
          <Panel title="Trial accounts (admin)" className="mt-3" testId="admin-trials-panel"
            action={<span className="text-[11px] text-muted-foreground num">
              {admin.summary.active_trials} active · {admin.summary.expired} expired · {admin.summary.converted} converted · {admin.summary.pending_verification} pending
            </span>}>
            <div className="overflow-x-auto max-h-[420px]">
              <table className="w-full dense-table">
                <thead className="sticky-head">
                  <tr><th>Name</th><th>Company</th><th>Email</th><th>Size</th><th>Trial start</th><th className="text-right">Days left</th><th>Status</th><th>Extend</th></tr>
                </thead>
                <tbody>
                  {admin.users.map((u) => (
                    <tr key={u.id} data-testid={`admin-trial-row-${u.id}`}>
                      <td>{u.name}</td>
                      <td className="text-muted-foreground max-w-[160px] truncate">{u.company}</td>
                      <td className="num text-[11px] text-muted-foreground">{u.email}</td>
                      <td className="text-muted-foreground">{u.company_size || "—"}</td>
                      <td className="num text-[11px]">{u.trial_started_at ? fmtDate(u.trial_started_at) : "—"}</td>
                      <td className="num text-right">{u.days_remaining ?? "—"}</td>
                      <td>
                        <Badge2 tone={u.status === "Converted" ? "good" : u.status === "Expired" ? "bad" : u.status === "Active Trial" ? "warn" : "muted"}>
                          {u.status}
                        </Badge2>
                      </td>
                      <td>
                        {u.trial_started_at ? (
                          <div className="flex gap-1">
                            {[7, 14].map((n) => (
                              <button key={n} onClick={() => extend(u.id, n)}
                                className="border border-border px-1.5 py-0.5 text-[11px] hover:border-primary hover:text-primary transition-colors duration-150"
                                data-testid={`extend-${u.id}-${n}`}>
                                +{n}d
                              </button>
                            ))}
                          </div>
                        ) : <span className="text-muted-foreground text-[11px]">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        ) : null}
      </div>
      <TrustBar />
      <Footer />
      <BackToTop />
    </div>
  );
}
